import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable, Injector, NgZone } from "@angular/core";
import { AuthService } from "@auth0/auth0-angular";
import { Observable, of, throwError } from "rxjs";
import { catchError, mergeMap } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { AnalyticsClientContextService } from "./analytics-client-context.service";
import { AuthSessionRecoveryService } from "./auth-session-recovery.service";

@Injectable()
class Auth0InterceptorApi implements HttpInterceptor {
  constructor(
    private injector: Injector,
    private analyticsContext: AnalyticsClientContextService,
    private authSessionRecovery: AuthSessionRecoveryService,
    private zone: NgZone
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.indexOf(environment.API_BASE_URL) !== 0) {
      return next.handle(req);
    }

    const analyticsHeaders = this.analyticsContext.getHeaders();
    if (this.isAnonymousPublicRequest(req)) {
      return this.runApiObservableInAngularZone(() => next.handle(req.clone({ setHeaders: analyticsHeaders })));
    }

    return this.runApiObservableInAngularZone(() => this.auth().getAccessTokenSilently().pipe(
      catchError((error) => {
        this.logTokenError(req, error);
        if (this.authSessionRecovery.recoverFromStaleSession(error)) {
          return throwError(error);
        }

        return of(null);
      }),
      mergeMap(token => {
        const headers = token
          ? Object.assign({}, analyticsHeaders, { Authorization: `Bearer ${token}` })
          : analyticsHeaders;

        return next.handle(req.clone({ setHeaders: headers }));
      })
    ));
  }

  private logTokenError(req: HttpRequest<any>, error: any): void {
    if (environment.production && localStorage.getItem(environment.DEBUG) !== "true") {
      return;
    }

    console.warn("[Auth0 interceptor] Access token lookup failed.", {
      method: req.method,
      url: this.safeUrl(req.url),
      error: {
        error: error && error.error,
        errorDescription: error && error.error_description,
        message: error && error.message
      },
      auth0SpaCacheKeys: this.auth0SpaCacheKeyCount()
    });
  }

  private safeUrl(url: string): string {
    try {
      const parsedUrl = new URL(url);
      return `${parsedUrl.origin}${parsedUrl.pathname}`;
    } catch (_) {
      return url ? url.split("?")[0] : "";
    }
  }

  private auth0SpaCacheKeyCount(): number {
    try {
      return Object.keys(localStorage)
        .filter((key) => key.indexOf("@@auth0spajs@@") === 0)
        .length;
    } catch (_) {
      return -1;
    }
  }

  private auth(): AuthService {
    return this.injector.get(AuthService);
  }

  private runApiObservableInAngularZone<T>(factory: () => Observable<T>): Observable<T> {
    return new Observable<T>((observer) => {
      let subscription: { unsubscribe: () => void } = null;

      this.runInAngularZone(() => {
        try {
          subscription = factory().subscribe(
            (value) => this.runInAngularZone(() => observer.next(value)),
            (error) => this.runInAngularZone(() => observer.error(error)),
            () => this.runInAngularZone(() => observer.complete())
          );
        } catch (error) {
          observer.error(error);
        }
      });

      return () => {
        if (subscription) {
          subscription.unsubscribe();
        }
      };
    });
  }

  private runInAngularZone<T>(callback: () => T): T {
    if (NgZone.isInAngularZone()) {
      return callback();
    }

    return this.zone.run(callback);
  }

  private isAnonymousPublicRequest(req: HttpRequest<any>): boolean {
    // These endpoints authenticate a narrowly scoped guest token, not a login session.
    // Completion/claim endpoints deliberately remain in the bearer-token path.
    const path = req.url.slice(environment.API_BASE_URL.length).split("?")[0];
    if ((req.method === "GET" && ["/guest-upgrades/current", "/guest-continuations/current"].includes(path)) ||
        (req.method === "POST" && ["/guest-upgrades/handoff", "/guest-upgrades/handoff/redeem"].includes(path)) ||
        (req.method === "POST" && /^\/guest-(?:upgrades|continuations)\/public-invite\/[^/]+\/participants\/[^/]+$/.test(path))) {
      return true;
    }
    if (req.headers.has("X-Guest-Owner-Token")) {
      return true;
    }

    const publicInviteUrl = environment.API_BASE_URL + "/public-event-invite/";
    const guestOwnerUrl = environment.API_BASE_URL + "/guest-owner/";
    const guestClaimUrl = environment.API_BASE_URL + "/player/guest/claim/";
    const publicProfileUrl = environment.API_BASE_URL + "/player/public/";
    const analyticsUrl = environment.API_BASE_URL + "/analytics/events";
    if (req.method === "POST" && req.url.indexOf(publicInviteUrl) === 0 && /\/me\/join(?:\?.*)?$/.test(req.url)) {
      return false;
    }
    if (req.method === "GET" && req.url.indexOf(publicInviteUrl) === 0 && /\/me(?:\?.*)?$/.test(req.url)) {
      return false;
    }

    return req.url.indexOf(publicInviteUrl) === 0 ||
      req.url.indexOf(guestOwnerUrl) === 0 ||
      (req.method === "GET" && req.url.indexOf(guestClaimUrl) === 0) ||
      (req.method === "GET" && req.url.indexOf(publicProfileUrl) === 0 && req.urlWithParams.indexOf("contextEventId=") === -1) ||
      req.url === analyticsUrl;
  }
}

export default Auth0InterceptorApi;
